/**
 * @file _survey-listener.js
 * @description Listens for 'survey:results:ready' event dispatched by the rules engine
 * survey (external repo) and saves the results to localStorage via EligibilityStorage.
 *
 * Acts as a bridge between the survey system and the eligibility display system. Call this on the survey page.
 *
 * @requires window.QLD.eligibilityStorage - EligibilityStorage singleton (from _eligibility-storage.js)
 *
 * Event detail structure:
 * {
 *   version: "1.0",
 *   surveyId: string, (optional)
 *   timestamp: string,
 *   facts: { q_citizenship_status, q_lives_in_qld, ... },
 *   results: { allowedGoals[], deniedGoals[], insufficientDataGoals[], allGoals[] },
 *   metadata: { completedAt, factCount, allowedGoalCount, deniedGoalCount, insufficientDataGoalCount }
 * }
 * Each goal: { goalId, status, meta: { title, description, url, tags[], ctas[], extra_meta } }
 *
 * Storage persists { id, status, title, target } per goal — full meta is NOT stored.
 */

(function () {
    'use strict';

    /**
     * Initialize the survey results listener
     */
    function initializeSurveyListener(surveyElement = null) {
        if (!window.QLD || !window.QLD.eligibilityStorage || !surveyElement) {
            return;
        }
        surveyElement.removeEventListener('survey:results:ready', handleSurveyResults);
        surveyElement.addEventListener('survey:results:ready', handleSurveyResults);
        
        surveyElement.removeEventListener('survey:early-exit:triggered', handleSurveyEarlyExit);
        surveyElement.addEventListener('survey:early-exit:triggered', handleSurveyEarlyExit);
    }

    /**
     * Handle survey results event
     */
    function handleSurveyResults(event) {
        try {
            if (!event.detail) {
                throw new Error('Event detail is missing');
            }
            validateEventData(event.detail);

            const result = window.QLD.eligibilityStorage.save(event.detail);

            if (!result.success) {
                console.error('[Survey Listener] Failed to save:', result.error);
            }
        } catch (error) {
            console.error('[Survey Listener] Failed to process survey results:', error);
        }
    }

    /**
     * Handle survey early exit event — clears any previously stored eligibility data
     */
    function handleSurveyEarlyExit() {
        try {
            if (window.QLD && window.QLD.eligibilityStorage) {
                window.QLD.eligibilityStorage.clear();
            }
        } catch (error) {
            console.error('[Survey Listener] Failed to process survey early exit:', error);
        }    
    }


    /**
     * Validate event data structure
     * @param {Object} detail - The event.detail object
     * @throws {Error} If required fields are missing or invalid
     */
    function validateEventData(detail) {
        const required = ['version', 'timestamp', 'facts', 'results', 'metadata'];
        const missing = required.filter(field => !detail[field]);

        if (missing.length > 0) {
            throw new Error(`Missing required fields: ${missing.join(', ')}`);
        }

        if (!detail.results.allGoals || !Array.isArray(detail.results.allGoals)) {
            throw new Error('results.allGoals must be an array');
        }
    }
    
    window.QLD = window.QLD || {};
    window.QLD.surveyListener = window.QLD.surveyListener || {};
    window.QLD.surveyListener.initialize = initializeSurveyListener;

})();